import { Button, Link, Text } from "@chakra-ui/react";
import { useTranslation } from "react-i18next";
import { FiArrowRight, FiCheckCircle, FiXCircle } from "react-icons/fi";
import { Link as RouterLink } from "react-router-dom";
import {
  type BuyerBidDetailDto,
  useBeginEvaluationMutation,
  useGetBidForBuyerQuery,
} from "../../../autogen/bff-api";
import { useApiError } from "../../../common/errors/useApiError";
import { useConfirmationModal } from "../../../common/useConfirmationModal";
import { urls } from "../../../urls";
import { RejectBidButton } from "../bid-detail/BidRejectionActionCard";
import { useBidQualificationDecision } from "../bid-detail/useBidQualificationDecision";
import { useUndoRejectionDialog } from "../bid-detail/useUndoRejectionDialog";

interface Props {
  eventId: string;
  bidId: string;
  allDecided: boolean;
  onDecided: () => void;
  onCompleted: () => void;
}

export const QualificationFocusActions = ({
  eventId,
  bidId,
  allDecided,
  onDecided,
  onCompleted,
}: Props) => {
  const { data: detail } = useGetBidForBuyerQuery({ eventId, bidId });
  if (!detail) return null;
  return (
    <Actions
      detail={detail}
      allDecided={allDecided}
      onDecided={onDecided}
      onCompleted={onCompleted}
    />
  );
};

const Actions = ({
  detail,
  allDecided,
  onDecided,
  onCompleted,
}: {
  detail: BuyerBidDetailDto;
  allDecided: boolean;
  onDecided: () => void;
  onCompleted: () => void;
}) => {
  const { t } = useTranslation();
  const { decision, decisionOpen, qualificationRoundOpen, pending, assessmentRequired, decide, isLoading } =
    useBidQualificationDecision(detail);
  const rejection = useUndoRejectionDialog(detail);
  const isRejected = rejection.isRejected;

  const qualify = async () => {
    await decide(true);
    onDecided();
  };

  return (
    <>
      {rejection.modal}
      {isRejected ? (
        <Button
          data-testid="focus-decision-unreject"
          colorScheme="red"
          variant="outline"
          leftIcon={<FiXCircle />}
          isLoading={rejection.isLoading}
          isDisabled={!rejection.canRevoke}
          onClick={rejection.canRevoke ? () => rejection.open() : undefined}
        >
          {rejection.canRevoke ? t("Rejected — undo") : t("Rejected")}
        </Button>
      ) : (
        <>
          <Button
            data-testid="focus-decision-qualify"
            colorScheme="green"
            leftIcon={<FiCheckCircle />}
            isLoading={isLoading}
            isDisabled={!decisionOpen || (!decision && (pending > 0 || assessmentRequired))}
            onClick={decision ? () => decide(false) : qualify}
          >
            {decision && decisionOpen ? t("Qualified — undo") : t("Qualified")}
          </Button>
          {decisionOpen && (
            <RejectBidButton
              detail={detail}
              width="auto"
              label={String(t("Reject"))}
              onRejected={onDecided}
            />
          )}
        </>
      )}

      {qualificationRoundOpen && allDecided && (
        <CompleteQualificationButton detail={detail} onCompleted={onCompleted} />
      )}

      {decisionOpen && !decision && !isRejected && pending > 0 && (
        <Text fontSize="xs" color="smMuted">
          {t("Review all qualification requirements first ({{count}} remaining)", { count: pending })}
        </Text>
      )}

      {decisionOpen && !decision && !isRejected && assessmentRequired && (
        <Text fontSize="xs" color="smMuted">
          {t("Write the assessment of the exclusion grounds on the tender first")}{" "}
          <Link
            as={RouterLink}
            to={urls.app.sourcingEvents.bidDetail.go(detail.bid.forEvent, detail.bid.id)}
            color="smPrimary"
          >
            {t("Open the tender")}
          </Link>
        </Text>
      )}
    </>
  );
};

const CompleteQualificationButton = ({
  detail,
  onCompleted,
}: {
  detail: BuyerBidDetailDto;
  onCompleted: () => void;
}) => {
  const { t } = useTranslation();
  const { trigger } = useApiError();
  const [beginEvaluation, { isLoading }] = useBeginEvaluationMutation();
  const { show, modal } = useConfirmationModal();

  const complete = async () => {
    try {
      await beginEvaluation({ eventId: detail.bid.forEvent }).unwrap();
      onCompleted();
    } catch (e) {
      trigger(e);
    }
  };

  return (
    <>
      <Button
        data-testid="focus-complete-qualification"
        variant="outline"
        colorScheme="green"
        bg="smSuccessSoft"
        rightIcon={<FiArrowRight />}
        isLoading={isLoading}
        onClick={() =>
          show({
            title: String(t("Complete qualification")),
            description: String(
              t(
                "The qualification is locked and the evaluation of the tenders opens. A rejection can still be undone until the award decision.",
              ),
            ),
            confirmButtonText: String(t("Complete qualification")),
            confirmButtonColor: "teal",
            size: "md",
            onConfirmed: complete,
          })
        }
      >
        {t("Complete the qualification")}
      </Button>
      {modal}
    </>
  );
};
